import { z } from 'zod';
import { ROLES } from '../permissions';

export const ACTIVITY_KINDS = ['Attendance', 'Workshop', 'Event', 'Exam'] as const;
export type ActivityKind = (typeof ACTIVITY_KINDS)[number];

export const GROUP_TYPES = ['School', 'Session', 'Event'] as const;
export type GroupType = (typeof GROUP_TYPES)[number];

export const PAYMENT_CHOICES = ['paid', 'later', 'waived'] as const;
export type PaymentChoice = (typeof PAYMENT_CHOICES)[number];

export const PAYMENT_STATUSES = ['free', 'paid', 'later', 'waived'] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export const PAYMENT_METHODS = ['mpesa', 'cash', 'card'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  mpesa: 'M-Pesa',
  cash: 'Cash',
  card: 'Card',
};

export const ARRIVALS = ['walkin', 'group', 'scan'] as const;
export type Arrival = (typeof ARRIVALS)[number];

const id = z.uuid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');
const hhmm = z.string().regex(/^\d{2}:\d{2}$/, 'Use HH:MM');
const pin = z.string().regex(/^\d{4}$/, 'PIN must be 4 digits');
const password = z.string().min(8, 'At least 8 characters').max(200);

// ---------- auth ----------
export const loginInput = z.object({
  email: z.email('Enter a valid email').trim().toLowerCase(),
  password: z.string().min(1, 'Enter your password'),
});
export type LoginInput = z.infer<typeof loginInput>;

/** A front-desk device's secret, issued when an admin turns front-desk mode on. */
const deskToken = z.string().min(20).max(200);

/** Front desk: list who can sign in on this device. */
export const deskRosterInput = z.object({ deskToken });

/** Front desk: tap your name, enter your PIN. */
export const pinLoginInput = z.object({ deskToken, staffId: z.string().min(1), pin });
export type PinLoginInput = z.infer<typeof pinLoginInput>;

export const changePinInput = z
  .object({ currentPin: pin, newPin: pin, confirmPin: z.string() })
  .refine((v) => v.newPin === v.confirmPin, { path: ['confirmPin'], message: "PINs don't match" })
  .refine((v) => v.newPin !== v.currentPin, { path: ['newPin'], message: 'Pick a different PIN' });
export type ChangePinInput = z.infer<typeof changePinInput>;

export const deskCreateInput = z.object({
  centerId: z.string().min(1),
  name: z.string().trim().min(1, 'Give this device a name').max(40),
});
export type DeskCreateInput = z.infer<typeof deskCreateInput>;

export const forgotInput = z.object({ email: z.email('Enter a valid email').trim().toLowerCase() });
export type ForgotInput = z.infer<typeof forgotInput>;

export const resetInput = z.object({ token: z.string().min(10), password });
export type ResetInput = z.infer<typeof resetInput>;

export const acceptInviteInput = z.object({
  name: z.string().trim().min(2, 'Enter your full name').max(80),
  password,
  pin,
});
export type AcceptInviteInput = z.infer<typeof acceptInviteInput>;

// ---------- students ----------
export const studentCreateInput = z.object({
  id,
  centerId: z.string().min(1),
  name: z.string().trim().min(1, 'Add a name first').max(80),
  school: z.string().trim().max(80).default(''),
  guardianName: z.string().trim().max(80).default(''),
  guardianPhone: z.string().trim().max(30).default(''),
  createdAt: z.iso.datetime().optional(),
});
export type StudentCreateInput = z.input<typeof studentCreateInput>;

export const studentPhotoInput = z.object({
  dataUrl: z
    .string()
    .regex(/^data:image\/(jpeg|png|webp);base64,/, 'Unsupported image')
    .max(1_500_000, 'Photo is too large'),
});

// ---------- activities ----------
export const activityCreateInput = z
  .object({
    id,
    centerId: z.string().min(1),
    name: z.string().trim().min(2, 'Name the activity').max(80),
    kind: z.enum(ACTIVITY_KINDS),
    date: isoDate.nullable(),
    time: hhmm.nullable(),
    feeMinor: z.number().int().min(0).max(100_000_000),
    capacity: z.number().int().min(0).max(100_000),
  })
  .refine((a) => a.kind === 'Attendance' || a.date !== null, { path: ['date'], message: 'Pick a date' });
export type ActivityCreateInput = z.infer<typeof activityCreateInput>;

// ---------- groups ----------
export const groupCreateInput = z.object({
  id,
  centerId: z.string().min(1),
  name: z.string().trim().min(2, 'Name the group').max(80),
  type: z.enum(GROUP_TYPES),
  studentIds: z.array(z.string()).max(500),
  teacherIds: z.array(z.string()).max(50).default([]),
});
export type GroupCreateInput = z.input<typeof groupCreateInput>;

// ---------- check-ins ----------
export const checkinCreateInput = z.object({
  id,
  centerId: z.string().min(1),
  studentId: z.string().min(1),
  activityId: z.string().min(1),
  arrival: z.enum(ARRIVALS),
  payment: z.enum(PAYMENT_CHOICES).nullable(),
  method: z.enum(PAYMENT_METHODS).nullable(),
  occurredAt: z.iso.datetime(),
});
export type CheckinCreateInput = z.infer<typeof checkinCreateInput>;

export const rollCallCreateInput = z.object({
  id,
  centerId: z.string().min(1),
  groupId: z.string().min(1),
  activityId: z.string().min(1),
  studentIds: z.array(z.string()).min(1, 'Tap at least one face').max(500),
  occurredAt: z.iso.datetime(),
});
export type RollCallCreateInput = z.infer<typeof rollCallCreateInput>;

export const settlePaymentInput = z.object({ method: z.enum(PAYMENT_METHODS) });

// ---------- team ----------
export const inviteCreateInput = z.object({
  email: z.email('Enter a valid email').trim().toLowerCase(),
  role: z.enum(ROLES),
  centerIds: z.array(z.string()).max(50),
});
export type InviteCreateInput = z.infer<typeof inviteCreateInput>;

// ---------- reports ----------
export const EXPORT_TYPES = ['attendance', 'payments', 'all'] as const;
export const EXPORT_RANGES = ['today', 'week', 'month'] as const;
export const EXPORT_SCOPES = ['center', 'all'] as const;
export const exportQuery = z.object({
  type: z.enum(EXPORT_TYPES).default('attendance'),
  range: z.enum(EXPORT_RANGES).default('today'),
  scope: z.enum(EXPORT_SCOPES).default('center'),
});
export type ExportQuery = z.infer<typeof exportQuery>;
